<template>
  <div id="player-performance-table">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <div class="subtitle-2">
          {{ $t('table_title.performance') }}
        </div>
        <div>
          <v-row
            align="center"
          >
            <v-col
              cols="auto"
              class="text-body-1 grey--text pl-2"
            >
              {{ $t('individual_analysis.performance.select_aggregation') }}
            </v-col>
            <v-col>
              <v-btn
                v-for="agg in aggregations"
                :key="'button'+agg.index"
                :text="selectedAggregation !== agg.value"
                :color="selectedAggregation === agg.value ? 'primary' : undefined"
                class="text-none"
                @click="selectedAggregation = agg.value"
              >
                {{ agg.text }}
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <variable-selector
                v-if="variables"
                :variables="variables"
                :dashboard="dashboard"
                category="GPS"
              />
            </v-col>
          </v-row>
        </div>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="tableHeaders"
          :loading="loading"
          :items="stats"
          hide-default-footer
          :items-per-page="pagination.per_page"
          dense
        >
          <!--<editor-fold desc="ITEM SLOT">-->
          <template v-slot:item="{ item }">
            <player-performance-table-row
              :data="item"
              :max-values="maxValues"
              :player="player"
              :aggregation="selectedAggregation"
              :variables="tableHeaders"
            />
          </template>
        </v-data-table>
        <div
          v-if="pagination && pagination.last_page && pagination.last_page > 1"
          class="d-flex justify-center align-center py-5"
        >
          <pagination
            v-model="pagination.current_page"
            :last-page="pagination.last_page"
          />
        </div>
      </v-card-text>
    </v-card>

    <v-row class="mt-3">
      <v-col />
      <v-col
        v-if="showMenstrualPeriod && xAxisMenstrualAnnotations.length > 0"
        cols="auto"
      >
        <v-btn
          small
          :color="!annotateMenstrual ? 'grey darken-2' : 'primary'"
          :outlined="!annotateMenstrual"
          class="text-none"
          @click="annotateMenstrual = !annotateMenstrual"
        >
          {{ $t('button.show_menstrual_phases') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-3 mb-6">
      <v-col>
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('UXPCSTDR'), label: $t('table_headers.weekly_load.total_duration'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('table_headers.weekly_load.total_duration') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col>
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('UXPCSTD'), label: $t('card_title.total_distance'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.total_distance') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CALETLDLHSR'), label: $t('card_title.high_speed_running'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.high_speed_running') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CALETLDLSP'), label: $t('card_title.sprint_distance'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.sprint_distance') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CACTCSVB3TD'), label: $t('card_title.speed_zone_14.4_19.8'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.speed_zone_14.4_19.8') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CACGCSMXV'), label: $t('card_title.maximal_player_velocities'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.maximal_player_velocities') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CAPGCSMXAC'), label: $t('card_title.maximal_player_accelerations'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.maximal_player_accelerations') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col cols="4">
        <section class="fill-height">
          <line-chart
            class="fill-height"
            :raw-data="stats"
            :chart-value-keys="[ { key: getVariableKey('CAPGCSMXDC'), label: $t('card_title.minimal_player_decelerations'), type: 'line' } ]"
            x-value="date"
            y-value-prefix="stats."
            :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.minimal_player_decelerations') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PlayerPerformanceTableRow from '@/components/players/PlayersPerformanceTableRow'
import Pagination from '@/components/Pagination'
import LineChart from '@/components/charts/LineChart.vue'
import { orderBy } from 'lodash'
import i18n from '@/plugins/i18n'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { DashboardType } from '@/data/models/Dashboard'

const aggregations = [
  {
    text: i18n.t('individual_analysis.performance.aggregation.matches'),
    value: '_G_60_ALL',
    index: 0
  },
  {
    text: i18n.t('individual_analysis.performance.aggregation.day'),
    value: '_1',
    index: 1
  },
  {
    text: i18n.t('individual_analysis.performance.aggregation.week'),
    value: '_week',
    index: 2
  },
  {
    text: i18n.t('individual_analysis.performance.aggregation.14_days'),
    value: '_14',
    index: 3
  },
  {
    text: i18n.t('individual_analysis.performance.aggregation.14_days.stdev'),
    value: '_stdev',
    index: 4
  },
  {
    text: i18n.t('individual_analysis.performance.aggregation.30_days_average'),
    value: '_30_avg',
    index: 5
  }
]

export default {
  name: 'PlayerPerformanceTable',
  components: {
    VariableSelector,
    LineChart,
    Pagination,
    PlayerPerformanceTableRow
  },
  props: {
    stats: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({})
    },
    maxValues: {
      type: Object,
      default: () => ({})
    },
    player: {
      type: Object,
      default: () => ({})
    },
    xAxisMenstrualAnnotations: {
      type: Array,
      required: true
    },
    showMenstrualPeriod: {
      type: Boolean,
      required: true
    },
    tableHeaders: {
      type: Array,
      required: true
    },
    variables: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      aggregations: aggregations,
      aggregations_list: ['_G_60_ALL', '_1', '_week', '_14', '_stdev', '_30_avg'],
      selectedAggregation: '_1',
      annotateMenstrual: false,
      dashboard: DashboardType.INDIVIDUAL_PERFORMANCE
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  created() {
  },
  methods: {
    orderBy,
    getVariableKey(variable) {
      return variable + this.selectedAggregation
    },
    getVariableName(variable) {
      return variable + this.aggregations_list[this.selectedAggregation]
    }
  }
}
</script>
